import { useState } from 'react';
import { X, LogIn, Mail, Lock, User, RefreshCw, Loader2 } from 'lucide-react';
import { authService } from '../lib/supabase';

// SVG icons for OAuth providers
const GoogleIcon = () => (
    <svg viewBox="0 0 24 24" className="size-5">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
    </svg>
);

const DiscordIcon = () => (
    <svg viewBox="0 0 24 24" className="size-5" fill="#5865F2">
        <path d="M20.317 4.492c-1.53-.69-3.17-1.2-4.885-1.49a.075.075 0 0 0-.079.036c-.21.369-.444.85-.608 1.23a18.566 18.566 0 0 0-5.487 0 12.36 12.36 0 0 0-.617-1.23A.077.077 0 0 0 8.562 3c-1.714.29-3.354.8-4.885 1.491a.07.07 0 0 0-.032.027C.533 9.093-.32 13.555.099 17.961a.08.08 0 0 0 .031.055 20.03 20.03 0 0 0 5.993 2.98.078.078 0 0 0 .084-.026 13.83 13.83 0 0 0 1.226-1.963.074.074 0 0 0-.041-.104 13.175 13.175 0 0 1-1.872-.878.075.075 0 0 1-.008-.125c.126-.093.252-.19.372-.287a.075.075 0 0 1 .078-.01c3.927 1.764 8.18 1.764 12.061 0a.075.075 0 0 1 .079.009c.12.098.245.195.372.288a.075.075 0 0 1-.006.125c-.598.344-1.22.635-1.873.877a.075.075 0 0 0-.041.105c.36.687.772 1.341 1.225 1.962a.077.077 0 0 0 .084.028 19.963 19.963 0 0 0 6.002-2.981.076.076 0 0 0 .032-.054c.5-5.094-.838-9.52-3.549-13.442a.06.06 0 0 0-.031-.028zM8.02 15.278c-1.182 0-2.157-1.069-2.157-2.38 0-1.312.956-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.956 2.38-2.157 2.38zm7.975 0c-1.183 0-2.157-1.069-2.157-2.38 0-1.312.955-2.38 2.157-2.38 1.21 0 2.176 1.077 2.157 2.38 0 1.312-.946 2.38-2.157 2.38z" />
    </svg>
);

const TwitchIcon = () => (
    <svg viewBox="0 0 24 24" className="size-5" fill="#9146FF">
        <path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714z" />
    </svg>
);



interface AuthModalProps {
    isOpen: boolean;
    onClose: () => void;
}

export function AuthModal({ isOpen, onClose }: AuthModalProps) {
    const [mode, setMode] = useState<'signin' | 'signup' | 'resend'>('signin');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [displayName, setDisplayName] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(false);
    const [oauthLoading, setOauthLoading] = useState<string | null>(null);
    const [rememberMe, setRememberMe] = useState<boolean>(() => {
        return localStorage.getItem('deckstr_remember_me') !== 'false';
    });

    if (!isOpen) return null;

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setSuccess('');
        setLoading(true);

        try {
            if (mode === 'signin') {
                await authService.signInEmail(email, password);
                onClose();
            } else if (mode === 'signup') {
                await authService.signUpEmail(email, password, displayName);
                setSuccess('Check your email to confirm your account!');
            } else if (mode === 'resend') {
                await authService.resendVerification(email);
                setSuccess('Verification email sent! Check your inbox.');
            }
        } catch (err: any) {
            setError(err.message || 'Authentication failed');
        } finally {
            setLoading(false);
        }
    };

    const handleOAuthSignIn = async (provider: 'google' | 'discord' | 'twitch' | 'twitter') => {
        setError('');
        setOauthLoading(provider);

        try {
            await authService.signInOAuth(provider);
            // OAuth redirects, so we don't need to close the modal
        } catch (err: any) {
            setError(err.message || `Failed to sign in with ${provider}`);
            setOauthLoading(null);
        }
    };

    const oauthProviders = [
        { id: 'google' as const, name: 'Google', icon: GoogleIcon, bgClass: 'bg-white hover:bg-gray-100', textClass: 'text-gray-700' },
        { id: 'discord' as const, name: 'Discord', icon: DiscordIcon, bgClass: 'bg-[#5865F2] hover:bg-[#4752C4]', textClass: 'text-white' },
        { id: 'twitch' as const, name: 'Twitch', icon: TwitchIcon, bgClass: 'bg-[#9146FF] hover:bg-[#7C2FE8]', textClass: 'text-white' },
    ];

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-[100] p-4">
            <div className="bg-slate-900 border border-white/10 rounded-xl p-6 w-full max-w-md relative overflow-hidden">
                {/* Gradient accent */}
                <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-purple-500 via-cyan-500 to-amber-500" />

                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-white/40 hover:text-white/60 transition-colors"
                >
                    <X className="size-5" />
                </button>

                <div className="text-center mb-6">
                    {mode === 'resend' ? (
                        <RefreshCw className="size-10 text-amber-400 mx-auto mb-2" />
                    ) : (
                        <LogIn className="size-10 text-cyan-400 mx-auto mb-2" />
                    )}
                    <h2 className="text-xl font-bold text-white">
                        {mode === 'signin' ? 'Welcome Back' : mode === 'signup' ? 'Create Account' : 'Resend Verification'}
                    </h2>
                    <p className="text-white/50 text-sm mt-1">
                        {mode === 'signin'
                            ? 'Sign in to save decks and access features'
                            : mode === 'signup'
                                ? 'Join DECKSTR.GG to save and share decks'
                                : 'Enter your email to receive a new verification link'}
                    </p>
                </div>

                {/* OAuth Buttons - Only show on signin/signup modes */}
                {mode !== 'resend' && (
                    <>
                        <div className="grid grid-cols-2 gap-3 mb-6">
                            {oauthProviders.map((provider) => (
                                <button
                                    key={provider.id}
                                    onClick={() => handleOAuthSignIn(provider.id)}
                                    disabled={oauthLoading !== null}
                                    className={`flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg font-medium transition-all ${provider.bgClass} ${provider.textClass} disabled:opacity-50 disabled:cursor-not-allowed`}
                                >
                                    {oauthLoading === provider.id ? (
                                        <Loader2 className="size-5 animate-spin" />
                                    ) : (
                                        <>
                                            <provider.icon />
                                            <span className="text-sm">{provider.name}</span>
                                        </>
                                    )}
                                </button>
                            ))}
                        </div>

                        <div className="flex items-center gap-4 mb-6">
                            <div className="flex-1 h-px bg-white/10" />
                            <span className="text-white/40 text-xs uppercase tracking-wider">or continue with email</span>
                            <div className="flex-1 h-px bg-white/10" />
                        </div>
                    </>
                )}

                <form onSubmit={handleSubmit} className="space-y-4">
                    {mode === 'signup' && (
                        <div className="relative">
                            <User className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-white/40" />
                            <input
                                type="text"
                                placeholder="Display Name"
                                value={displayName}
                                onChange={(e) => setDisplayName(e.target.value)}
                                className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-500/50 transition-colors"
                            />
                        </div>
                    )}

                    <div className="relative">
                        <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-white/40" />
                        <input
                            type="email"
                            placeholder="Email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-500/50 transition-colors"
                        />
                    </div>

                    {mode !== 'resend' && (
                        <>
                            <div className="relative">
                                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-white/40" />
                                <input
                                    type="password"
                                    placeholder="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    minLength={6}
                                    className="w-full pl-10 pr-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-white/30 focus:outline-none focus:border-cyan-500/50 transition-colors"
                                />
                            </div>

                            <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer pt-0.5 select-none">
                                <input
                                    type="checkbox"
                                    checked={rememberMe}
                                    onChange={(e) => {
                                        setRememberMe(e.target.checked);
                                        localStorage.setItem('deckstr_remember_me', e.target.checked ? 'true' : 'false');
                                    }}
                                    className="rounded border-white/20 bg-white/5 text-cyan-500 focus:ring-cyan-500/50 cursor-pointer"
                                />
                                <span>Remember me on this device</span>
                            </label>
                        </>
                    )}

                    {error && (
                        <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                            <p className="text-red-400 text-sm">{error}</p>
                        </div>
                    )}

                    {success && (
                        <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                            <p className="text-green-400 text-sm">{success}</p>
                        </div>
                    )}

                    <button
                        type="submit"
                        disabled={loading}
                        className="w-full py-3 bg-gradient-to-r from-cyan-500 to-blue-600 text-white rounded-lg font-medium hover:from-cyan-600 hover:to-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
                    >
                        {loading ? (
                            <>
                                <Loader2 className="size-4 animate-spin" />
                                Loading...
                            </>
                        ) : (
                            mode === 'signin' ? 'Sign In' : mode === 'signup' ? 'Create Account' : 'Send Verification Email'
                        )}
                    </button>
                </form>

                <div className="mt-4 text-center text-sm text-white/50 space-y-1">
                    {mode === 'signin' ? (
                        <>
                            <p>Don't have an account? <button onClick={() => { setMode('signup'); setError(''); setSuccess(''); }} className="text-cyan-400 hover:underline">Sign Up</button></p>
                            <p>Didn't get verification email? <button onClick={() => { setMode('resend'); setError(''); setSuccess(''); }} className="text-amber-400 hover:underline">Resend</button></p>
                        </>
                    ) : mode === 'signup' ? (
                        <>
                            <p>Already have an account? <button onClick={() => { setMode('signin'); setError(''); setSuccess(''); }} className="text-cyan-400 hover:underline">Sign In</button></p>
                            <p>Didn't get verification email? <button onClick={() => { setMode('resend'); setError(''); setSuccess(''); }} className="text-amber-400 hover:underline">Resend</button></p>
                        </>
                    ) : (
                        <p>Back to <button onClick={() => { setMode('signin'); setError(''); setSuccess(''); }} className="text-cyan-400 hover:underline">Sign In</button></p>
                    )}
                </div>

                {/* Account linking info */}
                {mode !== 'resend' && (
                    <div className="mt-4 p-3 bg-white/5 rounded-lg border border-white/10">
                        <p className="text-xs text-white/40 text-center">
                            🔗 Accounts with the same verified email are automatically linked
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
}
